import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import React, { forwardRef, useEffect, useState } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import { Link } from "@repo/i18n/routing";
import { ServicesRepository } from "@repo/fetch-repository/services";
import { ProjectsRepository } from "@repo/fetch-repository/projects";
import { ProductsRepository } from "@repo/fetch-repository/products";
import { useRouter } from "next/router";
import { Loader2Icon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useApp } from "@repo/app/useApp";
import { SettingsRepository } from "@repo/fetch-repository/settings";
import type { Social } from "@repo/fetch-repository/settings";
import { MediaRepository } from "@repo/fetch-repository/media";
import CroppedSection from "../components/common/cropped-section";

export const FooterDefinition: ComponentSettingsProps = {
  title: {
    type: "string",
    label: "عنوان",
  },
  description: {
    type: "string",
    label: "توضیحات",
  },
};

export interface FooterProps {
  title: string;
  description: string;
}

interface FooterItem {
  title: string;
  href?: string;
}

interface FooterColumn {
  title: string;
  items?: FooterItem[];
  href?: string;
}

export const FooterComponent = forwardRef<HTMLDivElement, FooterProps>(
  (props, ref): React.JSX.Element => {
    const app = useApp();
    const isEditor = app.rendererMode === "editor";
    const router = useRouter();
    const [socialItems, setSocialItems] = useState<Social[] | null>(null);
    const [socialItemsWithIcon, setSocialItemsWithIcon] = useState<
      | {
          name: string;
          link: string;
          icon?: string;
        }[]
      | null
    >(null);
    const locale =
      app.rendererMode === "editor"
        ? (router.query.locale as string)
        : router.locale || "en";
    const [loading, setLoading] = useState(true);
    const [footerItems, setFooterItems] = useState<{
      products: FooterColumn | null;
      projects: FooterColumn | null;
      services: FooterColumn | null;
    }>({
      products: null,
      projects: null,
      services: null,
    });
    // fetch images for icon
    const mediaRepository = new MediaRepository(locale);
    useEffect(() => {
      if (!socialItems) return;

      const fetchIcons = async (): Promise<void> => {
        const withIcons = await Promise.all(
          socialItems.map(async (social) => ({
            link: social.link,
            name: social.name,
            icon: (await mediaRepository.get(social.icon)).data?.url,
          }))
        );

        setSocialItemsWithIcon(withIcons);
      };

      void fetchIcons();
    }, [socialItems]);
    useEffect(() => {
      const settingsRepository: SettingsRepository = new SettingsRepository(
        locale
      );
      const productsRepository: ProductsRepository = new ProductsRepository(
        locale
      );
      const projectsRepository: ProjectsRepository = new ProjectsRepository(
        locale
      );
      const servicesRepository: ServicesRepository = new ServicesRepository(
        locale
      );
      setLoading(false);
      void Promise.all([
        productsRepository.getMany({ per_page: 3 }),
        projectsRepository.getMany({ per_page: 3 }),
        servicesRepository.getMany({ per_page: 3 }),
        settingsRepository.getMany(),
      ]).then(([products, projects, services, settings]) => {
        if (
          products.succeeded &&
          projects.succeeded &&
          services.succeeded &&
          settings.succeeded
        ) {
          setSocialItems(
            settings.data?.find((setting) => setting.key === "socials")
              ?.value as Social[]
          );
          setFooterItems({
            products: {
              title: locale === "fa" ? "کاربردها" : "Products",
              href: "/products",
              items:
                products.data?.map((product) => ({
                  title: product.name,
                  href: `/products/${product.id}/${encodeURIComponent(
                    product.name.replace(/\s+/g, "-").toLowerCase()
                  )}/`,
                })) || [],
            },
            projects: {
              title: locale === "fa" ? "پروژه ها" : "Projects",
              href: "/projects",
              items:
                projects.data?.map((project) => ({
                  title: project.name,
                  href: `/projects/${project.id}/${encodeURIComponent(
                    project.name.replace(/\s+/g, "-").toLowerCase()
                  )}/`,
                })) || [],
            },
            services: {
              title: locale === "fa" ? "خدمات" : "Services",
              href: "/services",
              items:
                services.data?.map((service) => ({
                  title: service.name,
                  href: `/services/${service.id}/${encodeURIComponent(
                    service.name.replace(/\s+/g, "-").toLowerCase()
                  )}/`,
                })) || [],
            },
          });
        }
      });
    }, [locale]);
    return (
      <>
        <div className="relative -z-30 scale-[-1] -mb-[27.3%]">
          <CroppedSection fill="#fff" />
        </div>
        <div className="relative -z-10 -mb-[13%]">
          <CroppedSection fill="#f7f8fc" />
        </div>
        <div
          className="py-8 relative md:pb-0 md:pt-11 lg:pt-16 w-full h-full flex flex-col gap-y-11"
          ref={ref}
        >
          <div className="bg-[#f7f8fc] absolute top-0 left-0 bottom-0 right-0 -z-40" />
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <Loader2Icon className="animate-spin" />
            </div>
          ) : (
            <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 container mx-auto gap-6 relative z-10">
              <div className="flex flex-col gap-[13px] md:gap-[17px] lg:gap-[25px] order-last md:order-first">
                <h3 className="font-helvetica rtl:font-doran text-[12.7px] md:text-[17px] lg:text-[24px] tracking-[0.08] md:tracking-[0.11px] lg:tracking-[0.14px] text-[#222]">
                  {props.title}
                </h3>
                <div className="flex flex-col gap-[10px] md:gap-[14px] lg:gap-[17px]">
                  <p className="font-helvetica rtl:font-doran text-[#666] text-[8.5px] md:text-[12px] lg:text-[16px] font-[500] leading-[2] tracking-normal">
                    {props.description}
                  </p>
                  {/* SOCIALS */}
                  {socialItemsWithIcon && socialItemsWithIcon.length > 0 ? (
                    <div className="flex flex-col gap-2 pt-4">
                      <h3 className="text-[19px] text-[#313131] uppercase rtl:font-doran rtl:font-bold">
                        {locale === "fa"
                          ? "شبکه های اجتماعی"
                          : "Social Networks"}
                      </h3>
                      <div className="flex gap-2">
                        {socialItemsWithIcon.map((social) => (
                          <Link
                            href={social.link}
                            key={social.name}
                            target="_blank"
                          >
                            <img
                              alt={social.name}
                              className="rounded-full size-8"
                              src={social.icon}
                            />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              {Object.entries(footerItems).map(([key, column]) => (
                <div
                  className="font-helvetica rtl:font-doran flex flex-col gap-[18px] md:gap-[24px] lg:gap-[34px]"
                  key={key}
                >
                  <Link
                    className="font-medium rtl:font-bold text-sm lg:text-xl leading-[1.44] text-[#222] hover:text-[#f70] transition-colors"
                    href={column?.href || "/"}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      if (isEditor) {
                        e.preventDefault();
                      }
                    }}
                  >
                    {column?.title}
                  </Link>
                  <div className="font-[100] rtl:font-normal text-xs lg:text-lg leading-[1.44] text-[#666] flex flex-col gap-[9px] md:gap-[14px] lg:gap-[17px]">
                    {column?.items?.map((item) => (
                      <Link
                        className="hover:text-[#f70] transition-colors"
                        href={item.href || "/"}
                        key={item.title}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                          if (isEditor) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="hidden md:flex container mx-auto border-t border-[#9999998f] py-7 lg:py-11 justify-between items-center text-[16px] text-[#666]">
            <p>Lorem ipsum dolor sit.</p>
            <p>Lorem, ipsum.</p>
          </div>
        </div>
      </>
    );
  }
);

FooterComponent.displayName = "Footer";

export const Footer = observer(
  FooterComponent
) as unknown as ComponentType<FooterProps>;
export const footerSettings: ComponentSettings = {
  name: "EFooter",
  props: FooterDefinition,
  label: "فوتر",
  widgets: {
    label: "فوتر",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EFooter", external: true }),
      }),
  },
};

Footer.componentSettings = footerSettings;
